import graphql__f8c06c1c653b39750a58 from "./__generated__/FinalInvoiceViewDownloadPdfMutation.graphql.ts";
import graphql__a1619c42bd5613d113df from "./__generated__/FinalInvoiceView_invoice.graphql.ts";
import graphql__ca9bc4dd3df72e2ea844 from "./__generated__/FinalInvoiceViewRefreshPdfMutation.graphql.ts";import * as React from "react";
import {useCallback, useEffect, useState} from "react";
import {graphql} from "react-relay";
import BreadcrumbNavigation from "./navigation/BreadcrumbNavigation";
import {useFragment, useRelayEnvironment} from "react-relay/hooks";
import InvoiceHeader from "./InvoiceHeader";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import {useOutletContext} from "react-router";
import Panel from "./presentational/Panel";
import {FormattedDate, useIntl} from "react-intl";
import DataListItem from "./presentational/DataListItem";
import PdfViewer from "./PdfViewer/PdfViewer";
import {FinalInvoiceViewDownloadPdfMutation} from "./__generated__/FinalInvoiceViewDownloadPdfMutation.graphql";
import ActionButton from "./presentational/button/ActionButton";
import {faFilePdf} from "@fortawesome/pro-solid-svg-icons";
import {FinalInvoiceViewRefreshPdfMutation} from "./__generated__/FinalInvoiceViewRefreshPdfMutation.graphql";
import {faBuildingColumns, faCalendarClock, faCreditCard, faEnvelope, faMoneyBillTransfer, faMoneyBillWave, faRectangleXmark, faSpinnerThird} from "@fortawesome/pro-regular-svg-icons";
import SendInvoiceDialog from "./dialog/SendInvoiceDialog";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import NoData from "./presentational/NoData";
import {parseISO} from "../helper/date";
import StatusBadge from "./presentational/StatusBadge";
import Dinero from "dinero.js";
import ActivityListItem from "./presentational/ActivityListItem";
import {faCcMastercard, faCcVisa} from "@fortawesome/free-brands-svg-icons";
import CreateReverseInvoiceDialog from "./dialog/CreateReverseInvoiceDialog";
import InitiateInvoicePaymentDialog from "./dialog/InitiateInvoicePaymentDialog";
import {useErrorContext} from "../contexts/ErrorContext";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";

type FinalInvoiceViewParams = {};

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type FinalInvoiceViewProps = FinalInvoiceViewParams & ConnectedProps<typeof connector>;

const FinalInvoiceView: React.FC<FinalInvoiceViewProps> = (props) => {
    const intl = useIntl();
    const outletContext: any = useOutletContext();
    const {queryRef, refetch} = outletContext;
    const relayEnvironment = useRelayEnvironment();
    const [invoicePdfUri, setInvoicePdfUri] = useState<string | undefined>(undefined);
    const [invoicePdfFilename, setInvoicePdfFilename] = useState<string | undefined>(undefined);
    const [invoicePdfUnavailable, setInvoicePdfUnavailable] = useState(false);
    const [invoicePdfRetried, setInvoicePdfRetried] = useState(false);
    const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
    const {addError} = useErrorContext();

    const {dispatchShowModal, dispatchHideModal} = props

    const getInvoiceStateLabel = (state: string): string => {
        switch (state) {
            case 'DRAFT':
                return intl.formatMessage({id: 'list.invoice.state.draft'});
            case 'INVOICE_NUMBER_PENDING':
                return intl.formatMessage({id: 'list.invoice.state.invoiceNumberPending'});
            case 'OPEN':
                return intl.formatMessage({id: 'list.invoice.state.open'});
            case 'PAID':
                return intl.formatMessage({id: 'list.invoice.state.paid'});
            case 'REFUNDED':
                return intl.formatMessage({id: 'list.invoice.state.refunded'});
            case 'REVERSED':
                return intl.formatMessage({id: 'list.invoice.state.reversed'});
            case 'UNCOLLECTIBLE':
                return intl.formatMessage({id: 'list.invoice.state.uncollectible'});
            case 'VOID':
                return intl.formatMessage({id: 'list.invoice.state.void'});
            default:
                return state;
        }
    };

    const handleDownloadPdf = (invoiceIdentifier: string, setIsDownloadingPdf) => {
        setIsDownloadingPdf(true)
        commitMutationWithErrorHandling<FinalInvoiceViewDownloadPdfMutation>(
            relayEnvironment,
            {
                mutation: graphql__f8c06c1c653b39750a58,
                variables: {
                    id: invoiceIdentifier
                },
                onCompleted: (response) => {
                    setIsDownloadingPdf(false)
                    if (response.downloadInvoicePdf?.uri) {
                        window.location.href = response.downloadInvoicePdf?.uri;
                    }
                },
                onError: () => {
                    setIsDownloadingPdf(false)
                }
            },
            addError,
            {
                customErrorMessage: "errors.failedToDownloadPdf"
            }
        )
    }

    const invoice = useFragment(
        graphql__a1619c42bd5613d113df,
        queryRef
    );

    // Note: The invoice document is up to date exactly when "version" equals "documentRenderedVersion".
    //       Timestamps must not be used for this: events like "paid", "sent" or "voided" bump "updatedAt"
    //       without touching the invoice data, so they deliberately leave "version" alone and do not
    //       trigger a re-rendering (see InvoiceProjector::whenInvoiceWasPaid() and friends).
    const hasRenderedDocument = invoice.documentSha1 !== null && invoice.documentSha1 !== '';
    const isDocumentOutdated = invoice.documentRenderedVersion !== null && invoice.version !== invoice.documentRenderedVersion;

    const loadInvoicePdfUri = useCallback(() => {
        commitMutationWithErrorHandling<FinalInvoiceViewRefreshPdfMutation>(
            relayEnvironment,
            {
                mutation: graphql__ca9bc4dd3df72e2ea844,
                variables: {
                    id: invoice.id
                },
                onCompleted: (response, errors) => {
                    if (errors && errors.length > 0) {
                        console.error(errors);
                    }
                    const uri = response.downloadInvoicePdf?.uri;
                    setInvoicePdfUri(uri ?? undefined);
                    setInvoicePdfFilename(response.downloadInvoicePdf?.filename ?? undefined);
                    setInvoicePdfUnavailable(!uri);
                },
                onError: (error) => {
                    console.error(error);
                    setInvoicePdfUnavailable(true);
                }
            },
            addError,
            {
                customErrorMessage: "errors.failedToRefreshPdf"
            }
        )
    }, [invoice.id, relayEnvironment, addError]);

    // A freshly rendered document replaces the previous one, so the cached download URI is stale.
    useEffect(() => {
        setInvoicePdfUri(undefined);
        setInvoicePdfUnavailable(false);
        setInvoicePdfRetried(false);
    }, [invoice.documentSha1, invoice.documentRenderedVersion]);

    useEffect(() => {
        if (hasRenderedDocument && invoicePdfUri === undefined && !invoicePdfUnavailable) {
            loadInvoicePdfUri();
        }
    }, [hasRenderedDocument, invoicePdfUri, invoicePdfUnavailable, loadInvoicePdfUri]);

    // The download URI is only valid for a short time (see tokenLifetime in Settings.PrivateResources.yaml),
    // so a single retry with a freshly issued URI is attempted before giving up.
    const handleInvoicePdfLoadError = (error: Error) => {
        console.error('Failed to load invoice PDF', error);
        if (invoicePdfRetried) {
            setInvoicePdfUnavailable(true);
            return;
        }
        setInvoicePdfRetried(true);
        setInvoicePdfUri(undefined);
    };

    // Reloads the invoice data and asks for a fresh download URI, so the button actually retries
    // both possible causes: missing document data and an expired URI.
    const handleRefreshPreview = () => {
        setInvoicePdfUri(undefined);
        setInvoicePdfUnavailable(false);
        setInvoicePdfRetried(false);
        refetch();
    };

    const sentAt = invoice.sentAt ? parseISO(invoice.sentAt)?.toJSDate() : undefined;
    const dueDate = invoice.dueDate ? parseISO(invoice.dueDate)?.toJSDate() : undefined;
    const totalGrossAmount = Dinero({amount: invoice.totalGrossAmount});

    const hasPaymentIntent = invoice.paymentIntents && invoice.paymentIntents[0] !== undefined;
    const now = new Date();
    const isDueDateInFuture = dueDate && dueDate > now;

    let paymentScheduleStatus = '';

    if (invoice.state === 'PAID') {
        paymentScheduleStatus = 'PAID';
    } else if (hasPaymentIntent) {
        paymentScheduleStatus = 'PROCESSING';
    } else if (isDueDateInFuture) {
        paymentScheduleStatus = 'SCHEDULED';
    } else {
        paymentScheduleStatus = 'DUE';
    }


    let paymentTermsLabel = '';
    if (invoice.paymentMethod?.paymentTerms) {
        switch (invoice.paymentMethod.paymentTerms) {
            case 'UponReceipt':
                paymentTermsLabel = intl.formatMessage({id: 'paymentMethod.paymentTerms.uponReceipt'});
                break;
            case 'Net7':
                paymentTermsLabel = intl.formatMessage({id: 'invoice.payment.net7Days'});
                break;
            case 'Net15':
                paymentTermsLabel = intl.formatMessage({id: 'invoice.payment.net15Days'});
                break;
            case 'Net30':
                paymentTermsLabel = intl.formatMessage({id: 'invoice.payment.net30Days'});
                break;
            case 'Net60':
                paymentTermsLabel = intl.formatMessage({id: 'invoice.payment.net60Days'});
                break;
        }
    }

    const breadcrumbNavigationItems = [
        {label: intl.formatMessage({id: 'navigation.invoices'}), to: '/invoices'},
        {label: invoice.id, to: '/invoices/' + invoice.id},
    ]

    return (
        <article>
            <BreadcrumbNavigation items={breadcrumbNavigationItems}/>
            <InvoiceHeader invoice={invoice}/>
            <main className="mx-auto lg:px-8 grid grid-cols-8 gap-x-4 gap-y-8 py-8">
                <div className="col-span-5">
                    <Panel>
                        {isDocumentOutdated && (
                            <div className="flex items-center gap-2 rounded-md bg-blue-50 px-3 py-2 text-sm text-blue-800">
                                <FontAwesomeIcon icon={faSpinnerThird} spin={true}/>
                                {intl.formatMessage({id: 'invoice.document.outdated'})}
                            </div>
                        )}
                        <PdfViewer
                            file={invoicePdfUri}
                            filename={invoicePdfFilename ?? (invoice.invoiceNumber ?? invoice.id) + '.pdf'}
                            showDownload={false}
                            noData={(<NoData
                                headline=""
                                description={(
                                    <>
                                        {invoicePdfUnavailable
                                            ? intl.formatMessage({id: 'invoice.document.unavailable'})
                                            : intl.formatMessage({id: 'invoice.document.notRenderedYet'})}
                                        <div className="mt-4">
                                            <ActionButton label={intl.formatMessage({id: 'button.refresh'})} onClick={handleRefreshPreview}/>
                                        </div>
                                    </>
                                )}
                            />)}
                            error={(<NoData
                                headline=""
                                description={(
                                    <>
                                        {intl.formatMessage({id: 'invoice.document.loadFailed'})}
                                        <div className="mt-4">
                                            <ActionButton label={intl.formatMessage({id: 'button.refresh'})} onClick={handleRefreshPreview}/>
                                        </div>
                                    </>
                                )}
                            />)}
                            onLoadError={handleInvoicePdfLoadError}
                        />
                    </Panel>
                </div>
                <div className="col-span-3">
                    <Panel buttons={(
                        <>
                            {invoice.type === 'REGULAR' && (<ActionButton icon={faRectangleXmark} iconSize={"lg"} title={intl.formatMessage({id: 'button.reverseInvoice'})} onClick={() => {
                                dispatchShowModal({
                                    body: (<CreateReverseInvoiceDialog handleCloseDialog={dispatchHideModal} queryRef={invoice}/>)
                                });
                            }}/>)}
                            {invoice.type === 'REGULAR' && invoice.state === 'OPEN' && (<ActionButton icon={faMoneyBillWave} iconSize={"lg"} title={intl.formatMessage({id: 'button.initiatePayment'})} onClick={() => {
                                dispatchShowModal({
                                    body: (<InitiateInvoicePaymentDialog handleCloseDialog={dispatchHideModal} queryRef={invoice}/>)
                                });
                            }}/>)}
                            <ActionButton icon={faEnvelope} iconSize={"lg"} title={intl.formatMessage({id: 'button.sendToCustomer'})} onClick={() => {
                                dispatchShowModal({
                                    body: (<SendInvoiceDialog handleCloseDialog={dispatchHideModal} queryRef={invoice}/>)
                                });
                            }}/>
                            <ActionButton isSubmitting={isDownloadingPdf} icon={faFilePdf} iconSize={"lg"} title={intl.formatMessage({id: 'button.downloadPdf'})} onClick={() => {
                                handleDownloadPdf(invoice.id, setIsDownloadingPdf)
                            }}/>
                        </>
                    )}>
                        <div className="mt-6 space-y-4 border-t border-gray-200 py-2">
                            <dl>
                                <DataListItem label={intl.formatMessage({id: 'invoice.field.state'})} condensed={true}>
                                    <StatusBadge
                                        className="-ml-2"
                                        states={{green: ["PAID", "REFUNDED"], red: ["UNCOLLECTIBLE"], yellow: ["VOID", "REVERSED"]}}
                                        value={invoice.state}
                                        label={getInvoiceStateLabel(invoice.state)}
                                    />
                                </DataListItem>
                                <DataListItem label={intl.formatMessage({id: 'invoice.field.invoiceNumber'})} condensed={true}>{invoice.invoiceNumber ?? '-'}</DataListItem>
                                <DataListItem label={intl.formatMessage({id: 'invoice.field.invoiceDate'})} condensed={true}><FormattedDate value={invoice.invoiceDate}/></DataListItem>
                                <DataListItem label={intl.formatMessage({id: 'invoice.field.dueDate'})} condensed={true}><FormattedDate value={invoice.dueDate}/></DataListItem>
                                {invoice.relatedInvoice !== null && (<DataListItem label={intl.formatMessage({id: 'invoice.field.relatedInvoice'})} condensed={true}>{invoice.relatedInvoice.invoiceNumber ?? '-'}</DataListItem>)}
                                <DataListItem label={intl.formatMessage({id: 'customer.field.customerNumber'})} condensed={true}>{invoice.customerAccount.customerNumber}</DataListItem>
                                <DataListItem label={intl.formatMessage({id: 'customer.field.vatId'})} condensed={true}>{invoice.customerAccount.vatId ?? '-'}</DataListItem>
                                <DataListItem label={intl.formatMessage({id: 'invoice.field.totalGrossAmount'})} condensed={true}>{totalGrossAmount.toFormat()}</DataListItem>
                            </dl>
                        </div>
                        {sentAt && (
                            <div className="mt-6 space-y-4 border-t border-gray-200 py-2">
                                <ul className="-mb-8 mt-4">
                                    <ActivityListItem
                                        icon={faEnvelope}
                                        date={sentAt}
                                        states={{green: ["SENT"]}}
                                        state="SENT"
                                        isLastItem={true}
                                        datePreText={intl.formatMessage({id: 'invoice.email.wasSentOn'})}
                                    >{intl.formatMessage({id: 'invoice.email.label'})}</ActivityListItem>
                                </ul>
                            </div>
                        )}
                        {(invoice.paymentIntents[0] !== null) && (
                            <div className="mt-6 space-y-4 border-t border-gray-200 py-2">
                                <h3 className="text-sm font-medium text-gray-900 mt-4">{intl.formatMessage({id: 'invoice.section.payment'})}</h3>
                                <ul className="-mb-8 mt-4">
                                    <ActivityListItem
                                        icon={faCalendarClock}
                                        states={{green: ["PAID"], gray: ["PROCESSING", "SCHEDULED"], red: ["DUE"]}}
                                        state={paymentScheduleStatus}
                                        isLastItem={invoice.paymentIntents[0] === null}
                                    >
                                        <div className="flex flex-col">
                                            <span>{intl.formatMessage({id: 'invoice.payment.paymentMethod'})}</span>
                                            <span className="text-xs text-gray-500">{paymentTermsLabel}</span>
                                            <span className="text-xs text-gray-500">
                                                {invoice.paymentMethod.paymentMethodType === 'SepaDebit' ? intl.formatMessage({id: 'paymentMethod.SepaDebit'}) : ''}
                                                {invoice.paymentMethod.paymentMethodType === 'Card' ? intl.formatMessage({id: 'paymentMethod.Card'}) : ''}
                                                {invoice.paymentMethod.paymentMethodType === 'OnAccount' ? intl.formatMessage({id: 'paymentMethod.OnAccount'}) : ''}
                                                {invoice.paymentMethod.lastFourDigits && ` •••• ${invoice.paymentMethod.lastFourDigits}`}
                                            </span>
                                        </div>
                                    </ActivityListItem>
                                    {invoice.paymentIntents.map((paymentIntent, index) => {
                                        const createdAt = paymentIntent.createdAt ? parseISO(paymentIntent.createdAt)?.toJSDate() : undefined;
                                        const succeededAt = paymentIntent.succeededAt ? parseISO(paymentIntent.succeededAt)?.toJSDate() : undefined;
                                        const canceledAt = paymentIntent.canceledAt ? parseISO(paymentIntent.canceledAt)?.toJSDate() : undefined;
                                        const date = succeededAt ?? canceledAt ?? createdAt;
                                        const datePretext = succeededAt ? intl.formatMessage({id: 'invoice.payment.succeededAt'}) : canceledAt ? intl.formatMessage({id: 'invoice.payment.wasCanceledAt'}) : intl.formatMessage({id: 'invoice.payment.wasCreatedAt'});
                                        const camelize = str => str.toLowerCase().replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => chr.toUpperCase());

                                        return (
                                            <div key={index}>
                                                <ActivityListItem
                                                    key={index}
                                                    icon={faMoneyBillWave}
                                                    date={date}
                                                    states={{green: ["SUCCEEDED"], gray: ["PROCESSING", "PENDING"], yellow: ["REQUIRES_ACTION", "REQUIRES_CONFIRMATION", "REQUIRES_PAYMENT_METHOD"], red: ["CANCELED", "UNKNOWN"]}}
                                                    state={paymentIntent.status}
                                                    datePreText={datePretext}
                                                    isLastItem={paymentIntent.charges.length === 0}>
                                                    {intl.formatMessage({id: 'invoice.payment.paymentIntent'})} <span className="text-xs font-light text-gray-500">{paymentIntent.id}</span>
                                                </ActivityListItem>
                                                {paymentIntent.charges.map((charge, chargeIndex) => {
                                                        const date = charge.createdAt ? parseISO(charge.createdAt)?.toJSDate() : undefined;

                                                        let icon = faMoneyBillTransfer;
                                                        if (charge.paymentMethod.paymentMethodType === 'Card') {
                                                            if (charge.paymentMethod.cardBrand === 'Visa') {
                                                                icon = faCcVisa;
                                                            } else if (charge.paymentMethod.cardBrand === 'Mastercard') {
                                                                icon = faCcMastercard;
                                                            } else {
                                                                icon = faCreditCard;
                                                            }
                                                        } else if (charge.paymentMethod.paymentMethodType === 'SepaDebit') {
                                                            icon = faBuildingColumns;
                                                        }

                                                    const chargeWasDisputedMessage = charge.disputed ? (
                                                        <p><strong className="text-red-500">{intl.formatMessage({id: 'invoice.payment.chargeDisputed'})}</strong></p>
                                                    ) : (<></>)
                                                    const chargeWasRefundedMessage = charge.refunded ? (
                                                        <p><strong className="text-red-500">{intl.formatMessage({id: 'invoice.payment.chargeRefunded'})}</strong></p>
                                                    ) : (<></>)
                                                    const chargeFailureMessage = paymentIntent.chargeFailureMessage ? (
                                                        <p className="mt-3"><strong className="text-red-500">{intl.formatMessage({id: 'invoice.payment.chargeFailure.' + camelize(paymentIntent.chargeFailureCode), defaultMessage: paymentIntent.chargeFailureMessage})}</strong></p>
                                                    ) : (<></>)

                                                    return (
                                                        <ActivityListItem
                                                            key={chargeIndex}
                                                            icon={icon}
                                                            states={{green: ["SUCCEEDED"], gray: ["PENDING"], red: ["FAILED", "UNKNOWN"]}}
                                                            state={charge.state}
                                                            date={date}
                                                            datePreText={intl.formatMessage({id: 'invoice.payment.createdAt'})}
                                                            isLastItem={chargeIndex === paymentIntent.charges.length - 1}
                                                                details={
                                                                    charge.paymentMethod.paymentMethodType === 'SepaDebit' ?
                                                                        (
                                                                            <>
                                                                                <p>{intl.formatMessage({id: 'invoice.payment.sepaDirectDebitFrom'}, {lastFourDigits: charge.paymentMethod.lastFourDigits})}</p>
                                                                                {chargeWasDisputedMessage}
                                                                                {chargeWasRefundedMessage}
                                                                                {chargeFailureMessage}
                                                                            </>
                                                                        ) : charge.paymentMethod.paymentMethodType === 'Card' ?
                                                                            (
                                                                                <>
                                                                                    <p>{intl.formatMessage({id: 'invoice.payment.chargingCard'}, {cardBrand: charge.paymentMethod.cardBrand, lastFourDigits: charge.paymentMethod.lastFourDigits})}</p>
                                                                                    {chargeWasDisputedMessage}
                                                                                    {chargeWasRefundedMessage}
                                                                                    {chargeFailureMessage}
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <p>{charge.paymentMethod.paymentMethodType}</p>
                                                                                    {chargeWasDisputedMessage}
                                                                                    {chargeWasRefundedMessage}
                                                                                    {chargeFailureMessage}
                                                                                </>
                                                                            )
                                                                }>
                                                                {intl.formatMessage({id: 'invoice.payment.charge'})} <span className="text-xs font-light text-gray-500">{charge.id}</span>
                                                            </ActivityListItem>
                                                        );
                                                    }
                                                )}
                                            </div>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                        {invoice.refunds?.length > 0 && (
                            <div className="mt-6 space-y-4 border-t border-gray-200 py-2">
                                <ul className="-mb-8 mt-4">
                                    {invoice.refunds.map((refund, index) => {
                                        if (refund === null) {
                                            return (<div key={index}></div>);
                                        }
                                        const createdAt = refund.createdAt ? parseISO(refund.createdAt)?.toJSDate() : undefined;
                                        const succeededAt = refund.succeededAt ? parseISO(refund.succeededAt)?.toJSDate() : undefined;
                                        const date = succeededAt ?? createdAt;
                                        const datePretext = succeededAt ? intl.formatMessage({id: 'invoice.payment.succeededAt'}) : intl.formatMessage({id: 'invoice.payment.wasCreatedAt'});
                                        return (
                                            <div key={index}>
                                                <ActivityListItem
                                                    key={index}
                                                    icon={faMoneyBillWave}
                                                    date={date}
                                                    states={{green: ["SUCCEEDED"], gray: ["PENDING"], yellow: ["REQUIRES_ACTION"], red: ["CANCELED", "FAILED", "UNKNOWN"]}}
                                                    state={refund.status}
                                                    datePreText={datePretext}
                                                    isLastItem={true}>
                                                    {intl.formatMessage({id: 'invoice.payment.refund'})} <span className="text-xs font-light text-gray-500">{refund.id}</span>
                                                </ActivityListItem>
                                            </div>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </Panel>
                </div>
            </main>
        </article>
    );
}

export default connector(FinalInvoiceView);
